/**
 * Wire protocol between the Excalidraw board client and miro-sync
 * (`/sync/excalidraw/{boardId}?token=<jwt>`), per eval/MIRO_PLAN.md. All JSON.
 */

/** The fields of an Excalidraw element the protocol relies on; the rest ride along. */
export interface WireElement {
  id: string;
  version: number;
  versionNonce: number;
  isDeleted?: boolean;
  [k: string]: unknown;
}

export interface WireUser {
  socketId: string;
  userId: string;
  userName: string;
  color: string;
}

export interface WireFile {
  url: string;
  mimeType: string;
}

export type PointerTool = 'pointer' | 'laser';
export type IdleState = 'active' | 'away' | 'idle';

// ---- server → client ---------------------------------------------------------

export interface InitMsg {
  type: 'init';
  elements: WireElement[];
  users: WireUser[];
  files?: Record<string, WireFile>;
  /** This connection's socket id, when the server announces it. */
  socketId?: string;
  role?: 'owner' | 'editor' | 'viewer';
}
export interface UpdateMsg {
  type: 'update';
  elements: WireElement[];
  from?: string;
}
export interface UsersMsg {
  type: 'users';
  users: WireUser[];
}
export interface PointerMsg {
  type: 'pointer';
  socketId: string;
  userId: string;
  userName: string;
  color: string;
  pointer: { x: number; y: number; tool: PointerTool };
  button: 'up' | 'down';
  selectedElementIds?: Record<string, true>;
  /** Activity next to the cursor, e.g. "I LD01 DOWNLIGHT" (design estimate). */
  label?: string;
}
export interface IdleMsg {
  type: 'idle';
  socketId: string;
  userId?: string;
  userName?: string;
  color?: string;
  state: IdleState;
}
export interface FileMsg {
  type: 'file';
  fileId: string;
  url: string;
  mimeType: string;
}
export interface ErrorMsg {
  type: 'error';
  code?: string;
  error?: string;
  message?: string;
}

export type ServerMsg = InitMsg | UpdateMsg | UsersMsg | PointerMsg | IdleMsg | FileMsg | ErrorMsg;

// ---- client → server ---------------------------------------------------------

export type ClientMsg =
  | { type: 'update'; elements: WireElement[] }
  | {
      type: 'pointer';
      pointer: { x: number; y: number; tool: PointerTool };
      button: 'up' | 'down';
      selectedElementIds?: Record<string, true>;
      label?: string;
    }
  | { type: 'idle'; state: IdleState }
  | { type: 'file'; fileId: string; url: string; mimeType: string };

export function parseServerMsg(data: unknown): ServerMsg | null {
  if (typeof data !== 'string') return null;
  try {
    const m = JSON.parse(data) as { type?: unknown };
    if (!m || typeof m !== 'object' || typeof m.type !== 'string') return null;
    return m as ServerMsg;
  } catch {
    return null;
  }
}
